// pages/adoption/AdoptionPage.jsx - Adoption page with social sidebar
"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Import components
import AdoptionPostFeed from "../../components/adoption/AdoptionPostFeed";
import SelectPetForAdoptionModal from "../../components/modals/SelectPetForAdoptionModal";
import CreateAdoptionPostModal from "../../components/modals/CreateAdoptionPostModal";
import AdoptionRequestsModal from "../../components/modals/AdoptionRequestsModal";
import AdoptionPostDetailModal from "../../components/modals/AdoptionPostDetailModal";
import { useAuth } from "../../hooks/useAuth";
import { getUserPlaceholderImage } from "../../utils/imageUtils";
import { getUserTypeColor } from "../../utils/userTypeUtils";
import { adoptionAPI } from "../../services/adoptionAPI";
import { handleApiError } from "../../services/api";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import "../../styles/adoptionTheme.css";
import "./AdoptionPage.css";

const AdoptionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  // Helper: if translation key is missing, fall back to English copy
  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const [isLoading] = useState(false);
  const mountedRef = useRef(true);

  // Modal states
  const [showSelectPetModal, setShowSelectPetModal] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [showEditPostModal, setShowEditPostModal] = useState(false);
  const [showRequestsModal, setShowRequestsModal] = useState(false);
  const [selectedPets, setSelectedPets] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null); // For filtering requests by post

  // Post feed refresh trigger
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const [showPostDetailModal, setShowPostDetailModal] = useState(false);
  const [detailPost, setDetailPost] = useState(null);
  const adoptionPostFeedRef = useRef();

  // UNIFIED: Use single authentication hook
  const {
    user,
    isLoading: authLoading,
    getUserType,
    getDisplayName,
    getProfileImageUrl,
  } = useAuth();

  // Get user type color
  const userType = getUserType();
  const userTypeColor = getUserTypeColor(userType);

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Helper function to create a light tint (mix with white)
  const lightTint = (hex, percent) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    // Mix with white (255, 255, 255) based on percent
    const mixR = Math.round(r + (255 - r) * (1 - percent));
    const mixG = Math.round(g + (255 - g) * (1 - percent));
    const mixB = Math.round(b + (255 - b) * (1 - percent));
    return `rgb(${mixR}, ${mixG}, ${mixB})`;
  };

  // Calculate color variants for CSS variables
  const colorVariants = {
    main: userTypeColor,
    background8: hexToRgba(userTypeColor, 0.08),
    shadow20: hexToRgba(userTypeColor, 0.2),
    shadow10: hexToRgba(userTypeColor, 0.1),
    shadow30: hexToRgba(userTypeColor, 0.3),
    shadow15: hexToRgba(userTypeColor, 0.15),
    border30: hexToRgba(userTypeColor, 0.3),
    hoverBg: lightTint(userTypeColor, 0.95), // 95% white, 5% color
  };

  // Cleanup on unmount AND force body scroll to avoid locks
  useEffect(() => {
    mountedRef.current = true;

    // Force body to scrollable (fixes cases where other pages lock it)
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";

    return () => {
      mountedRef.current = false;
      // Restore original overflow only if we changed it, 
      // but usually safer to just leave it auto unless managing a transition
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  // Handle URL parameters to open modal and highlight request
  useEffect(() => {
    const openRequests = searchParams.get('openRequests');
    const highlightRequest = searchParams.get('highlightRequest');

    if (openRequests === 'true') {
      setShowRequestsModal(true);
      if (highlightRequest) {
        setHighlightedRequestId(highlightRequest);
        // Clear the URL parameters after reading them
        setSearchParams({});
      }
    }
  }, [searchParams, setSearchParams]);

  // Get current user data with better fallbacks
  const currentUser = useMemo(() => {
    if (!user) {
      return {
        id: 1,
        name: "Loading User",
        username: "@loading",
        profile_image_url: getUserPlaceholderImage(null),
        avatar: getUserPlaceholderImage(null),
        role: "Pet Guardian",
        stats: { posts: 0, friends: 0, following: 0, followers: 0 },
        friends_count: 0,
        following_count: 0,
        followers_count: 0,
      };
    }

    const baseName = getDisplayName();
    const profileImage = user.profile_image_url ||
      user.profile_image ||
      user.avatar ||
      user.image ||
      getProfileImageUrl() ||
      getUserPlaceholderImage(user);
    const friendsCount = user.friends_count ?? user.stats?.friends ?? 0;
    const postsCount = user?.stats?.posts || user?.posts_count || 0;
    const followingCount = user?.following_count || 0;
    const followersCount = user?.followers_count || 0;
    const userRole = getUserType()?.replace(/\b\w/g, (l) => l.toUpperCase()) || "Pet Guardian";

    return {
      id: user.id || user.user_id,
      name: baseName,
      username: user.username || `@${baseName.replace(/\s+/g, "").toLowerCase()}`,
      profile_image_url: profileImage,
      avatar: profileImage,
      role: userRole,
      sub_type: user.sub_type || user.subType,
      sub_type_display_name: user.sub_type_display_name || user.subTypeDisplayName,
      stats: {
        posts: postsCount,
        friends: friendsCount,
        following: followingCount,
        followers: followersCount,
      },
      friends_count: friendsCount,
      following_count: followingCount,
      followers_count: followersCount,
      posts_count: postsCount,
    };
  }, [user, getDisplayName, getProfileImageUrl, getUserType]);

  // Handle pet selection confirmation
  const handlePetSelectionConfirm = (pets) => {
    setSelectedPets(pets);
    setShowSelectPetModal(false);
    setShowCreatePostModal(true);
  };

  // Handle adoption post creation
  const handleCreateAdoptionPost = async (postData) => {
    try {
      const response = await adoptionAPI.createAdoptionPost(postData);
      const createdId =
        response?.data?.data?.id ||
        response?.data?.id ||
        response?.data?.data?.post?.id ||
        null;
      showSuccessToast(t('adoption.success.postCreated') || 'Adoption post created successfully!');
      setShowCreatePostModal(false);
      setSelectedPets([]);
      // Refresh the feed
      setRefreshTrigger(prev => prev + 1);
      setHighlightedPostId(createdId || 'newest');
    } catch (error) {
      const errorMessage = handleApiError(error);
      showErrorToast(errorMessage || t('adoption.errors.postCreationFailed') || 'Failed to create adoption post');
      throw error;
    }
  };

  // Handle edit post
  const handleEditPost = (post) => {
    setEditingPost(post);
    // Extract pets from post (use full pet objects, not just IDs)
    const pets = post.pet_info
      ? (Array.isArray(post.pet_info) ? post.pet_info : [post.pet_info])
      : [];
    setSelectedPets(pets);
    setShowEditPostModal(true);
  };

  // Handle update adoption post
  const handleUpdateAdoptionPost = async (postData) => {
    if (!editingPost) return;

    try {
      await adoptionAPI.updateAdoptionPost(editingPost.id, postData);
      showSuccessToast(t('adoption.success.postUpdated') || 'Adoption post updated successfully!');
      setShowEditPostModal(false);
      setEditingPost(null);
      setSelectedPets([]);
      // Refresh the feed
      setRefreshTrigger(prev => prev + 1);
      setHighlightedPostId(editingPost.id);
    } catch (error) {
      const errorMessage = handleApiError(error);
      showErrorToast(errorMessage || t('adoption.errors.postUpdateFailed') || 'Failed to update adoption post');
      throw error;
    }
  };

  // Handle adoption request accepted
  const handleRequestAccepted = () => {
    // Refresh the feed to update post statuses
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle adoption request rejected
  const handleRequestRejected = () => {
    // Update counts locally in the feed immediately
    if (adoptionPostFeedRef.current) {
      adoptionPostFeedRef.current.decrementRequestCount();
    }
    // Also refresh the feed to ensure data is in sync with backend
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle viewing requests for a specific post
  const handleViewPostRequests = (postId) => {
    setSelectedPostId(postId);
    setShowRequestsModal(true);
  };

  // Show loading state while authentication is loading
  if (authLoading || isLoading) {
    return (
      <div className="adoption-loading-container">
        <div className="adoption-loading-content">
          <div className="adoption-spinner"></div>
          <p className="adoption-loading-text">
            {authLoading
              ? t("socialFeed.loading.authenticating")
              : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="adoption-page-root"
      data-tour="adoption-page"
      dir={i18n.dir()}
      style={{
        '--adoption-accent': colorVariants.main,
        '--adoption-accent-soft': colorVariants.background8,
        '--adoption-accent-shadow': colorVariants.shadow20,
        '--adoption-user-type-color': colorVariants.main,
        '--adoption-user-type-bg-8': colorVariants.background8,
        '--adoption-user-type-shadow-20': colorVariants.shadow20,
        '--adoption-user-type-shadow-10': colorVariants.shadow10,
        '--adoption-user-type-shadow-30': colorVariants.shadow30,
        '--adoption-user-type-shadow-15': colorVariants.shadow15,
        '--adoption-user-type-border-30': colorVariants.border30,
        '--adoption-user-type-hover-bg': colorVariants.hoverBg,
      }}
    >
      {/* Main Content Area */}
      <main className="adoption-main-content">
        <div className="adoption-content-container">
          {/* Hero header with title, subtitle and primary actions */}
          <div className="adoption-header">
            <div className="adoption-header-title">
              <p className="adoption-eyebrow">
                {tr("adoption.page.eyebrow", "Safe, local pet rehoming")}
              </p>
              <h1 className="adoption-title">
                {t("adoption.page.title") || "Pet Adoption"}
              </h1>
              <p className="adoption-subtitle">
                {tr(
                  "adoption.page.subtitle",
                  "Create adoption posts for your pets and connect with responsible adopters in your country."
                )}
              </p>
            </div>
            <div className="adoption-header-actions">
              <button
                className="adoption-action-btn adoption-offer-btn"
                onClick={() => setShowSelectPetModal(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5V19M5 12H19" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("adoption.buttons.offerForAdoption") || "Offer for Adoption"}
              </button>
              <button
                className="adoption-action-btn adoption-requests-btn"
                onClick={() => setShowRequestsModal(true)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("adoption.buttons.adoptionRequests") || "Adoption Requests"}
              </button>
            </div>
          </div>

          {/* Adoption Posts Feed */}
          <AdoptionPostFeed
            ref={adoptionPostFeedRef}
            currentUser={currentUser}
            refreshTrigger={refreshTrigger}
            onEditPost={handleEditPost}
            onViewRequests={handleViewPostRequests}
            onOfferForAdoption={() => setShowSelectPetModal(true)}
            highlightedPostId={highlightedPostId}
            onViewDetails={(post) => {
              setDetailPost(post);
              setShowPostDetailModal(true);
            }}
          />
        </div>
      </main>

      {/* Modals */}
      <SelectPetForAdoptionModal
        isOpen={showSelectPetModal}
        onClose={() => setShowSelectPetModal(false)}
        onConfirm={handlePetSelectionConfirm}
      />

      <CreateAdoptionPostModal
        isOpen={showCreatePostModal}
        onClose={() => {
          setShowCreatePostModal(false);
          setSelectedPets([]);
        }}
        selectedPets={selectedPets}
        onSubmit={handleCreateAdoptionPost}
      />

      <CreateAdoptionPostModal
        isOpen={showEditPostModal}
        onClose={() => {
          setShowEditPostModal(false);
          setEditingPost(null);
          setSelectedPets([]);
        }}
        selectedPets={selectedPets}
        editingPost={editingPost}
        onSubmit={handleUpdateAdoptionPost}
      />

      <AdoptionRequestsModal
        isOpen={showRequestsModal}
        onClose={() => {
          setShowRequestsModal(false);
          setHighlightedRequestId(null);
          setSelectedPostId(null);
        }}
        onRequestAccepted={handleRequestAccepted}
        onRequestRejected={handleRequestRejected}
        highlightedRequestId={highlightedRequestId}
        postId={selectedPostId}
        currentUser={currentUser}
      />

      {showPostDetailModal && (
        <AdoptionPostDetailModal
          isOpen={showPostDetailModal}
          onClose={() => {
            setShowPostDetailModal(false);
            setDetailPost(null);
          }}
          postId={detailPost?.id}
          post={detailPost}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default AdoptionPage;

